import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { Subscription, finalize } from 'rxjs';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { ComponentDataDestService } from '../../../services/component-data-dest/component-data-dest.service';
import { UserService } from '../../../services/user/user.service';
import {
  ProfileEpWizardAssignmentDTO,
  ProfileEpWizardDataDTO,
  ProfileEpWizardPathDTO,
} from '../../../dto/educational-path/profile-ep-wizard.dto';

interface PathSelectionRow {
  path: ProfileEpWizardPathDTO;
  selected: boolean;
  version_id: number | null;
  isExisting: boolean;
}

@Component({
  selector: 'app-duna-user-profile-ep-wizard',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatStepperModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatCardModule,
    MatTableModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './duna-user-profile-ep-wizard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-user-profile-ep-wizard.component.scss',
})
export class DunaUserProfileEpWizardComponent implements OnInit, OnDestroy {
  @ViewChild('stepper') stepper?: MatStepper;

  showWizard = false;
  loading = false;
  submitting = false;
  error: string | null = null;
  canEdit = false;

  userUuid: string | null = null;
  wizardData: ProfileEpWizardDataDTO | null = null;
  pathRows: PathSelectionRow[] = [];

  datesForm: FormGroup;
  displayedColumns = ['select', 'name', 'version', 'status'];

  private subscriptions = new Subscription();

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private componentDataSourceService: ComponentDataSourceService,
    private componentDataDestService: ComponentDataDestService,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {
    this.datesForm = this.fb.group({
      requirement_started_at: ['2023-01-01', Validators.required],
      maintenance_started_at: ['2023-01-01', Validators.required],
    });
  }

  ngOnInit(): void {
    this.checkEditPermissions();
    this.resolveUserUuid();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  startWizard(): void {
    this.error = null;
    this.showWizard = true;
    this.cdr.markForCheck();
    this.loadWizardData();
  }

  cancelWizard(): void {
    this.showWizard = false;
    this.error = null;
    this.stepper?.reset();
  }

  get selectedRows(): PathSelectionRow[] {
    return this.pathRows.filter((r) => r.selected);
  }

  get hasValidSelection(): boolean {
    return (
      this.selectedRows.length > 0 &&
      this.selectedRows.every((r) => r.version_id != null && r.version_id > 0)
    );
  }

  toggleRow(row: PathSelectionRow, checked: boolean): void {
    row.selected = checked;
    const versions = row.path.versions ?? [];
    if (checked && row.version_id == null && versions.length > 0) {
      row.version_id = versions[0].version_id;
    }
  }

  onVersionChange(row: PathSelectionRow, versionId: number | string): void {
    row.version_id =
      typeof versionId === 'number'
        ? versionId
        : parseInt(String(versionId), 10);
  }

  submit(): void {
    if (!this.userUuid || !this.hasValidSelection || this.datesForm.invalid) {
      this.error =
        'Udfyld startdatoer og vælg mindst én uddannelsesvej med version.';
      return;
    }

    const dest = this.componentDataDestService.getDataDest(
      'profile_user_ep_assign'
    );
    if (!dest) {
      this.error = 'Gem-funktionen er ikke tilgængelig.';
      return;
    }

    this.submitting = true;
    this.error = null;

    const dates = this.datesForm.value;
    const payload = {
      userUuid: this.userUuid,
      requirement_started_at: dates.requirement_started_at,
      maintenance_started_at: dates.maintenance_started_at,
      assignments: this.selectedRows.map((r) => ({
        path_guid: r.path.guid,
        version_id: r.version_id as number,
      })),
    };

    const result$ = dest(payload);
    if (!result$ || typeof result$.subscribe !== 'function') {
      this.submitting = false;
      this.error = 'Uventet svar fra gem-funktionen.';
      return;
    }

    result$.subscribe({
      next: (response: {
        success_count?: number;
        failed?: Array<{ path_guid: string; reason: string }>;
      }) => {
        this.submitting = false;
        const failed = response?.failed ?? [];
        if (failed.length > 0) {
          const msg = failed
            .map((f) => `${f.path_guid}: ${f.reason}`)
            .join('; ');
          this.snackBar.open(
            `Gemt ${response.success_count ?? 0} vej(e). Fejl: ${msg}`,
            'Luk',
            { duration: 8000 }
          );
        } else {
          this.snackBar.open(
            `Uddannelsesveje gemt (${
              response.success_count ?? this.selectedRows.length
            }).`,
            'Luk',
            { duration: 4000 }
          );
          this.cancelWizard();
        }
        this.cdr.markForCheck();
      },
      error: (err: unknown) => {
        this.submitting = false;
        this.error =
          (err as { error?: { message?: string }; message?: string })?.error
            ?.message ??
          (err instanceof Error
            ? err.message
            : 'Der opstod en fejl ved gemning.');
        this.cdr.markForCheck();
      },
    });
  }

  getPathLabel(row: PathSelectionRow): string {
    return row.path.full_name || row.path.name;
  }

  getRowStatus(row: PathSelectionRow): string {
    if (row.isExisting) {
      return 'Eksisterende';
    }
    return row.selected ? 'Ny' : '';
  }

  getVersionLabel(row: PathSelectionRow): string {
    const match = (row.path.versions ?? []).find(
      (v) => v.version_id === row.version_id
    );
    return match?.display_name ?? String(row.version_id ?? '');
  }

  private resolveUserUuid(): void {
    this.subscriptions.add(
      this.route.params.subscribe((params) => {
        const uuidFromRoute = params['userUuid'];
        if (uuidFromRoute) {
          this.userUuid = uuidFromRoute;
          return;
        }
        const profileMatch = window.location.pathname.match(
          /\/profile\/([a-f0-9-]{36})/
        );
        if (profileMatch) {
          this.userUuid = profileMatch[1];
          return;
        }
        this.userUuid = this.userService.getCurrentUserUuid();
        if (!this.userUuid) {
          this.subscriptions.add(
            this.userService.getCurrentUserFromToken().subscribe((user) => {
              this.userUuid = user?.uuid ?? null;
            })
          );
        }
      })
    );
  }

  private checkEditPermissions(): void {
    this.subscriptions.add(
      this.userService.getCurrentUserFromToken().subscribe({
        next: (user) => {
          if (!user?.roles) {
            this.canEdit = false;
            return;
          }
          const roleNames = this.extractRoleNames(user.roles);
          const allowedRoles = ['superadmin', 'orgadmin', 'orgbruger'];
          this.canEdit = allowedRoles.some((role) => roleNames.includes(role));
          this.cdr.markForCheck();
        },
        error: () => {
          this.canEdit = false;
          this.cdr.markForCheck();
        },
      })
    );
  }

  private extractRoleNames(roles: unknown): string[] {
    const roleNames: string[] = [];
    if (Array.isArray(roles)) {
      roles.forEach((role: unknown) => {
        if (typeof role === 'string') {
          roleNames.push(role);
        } else if (typeof role === 'object' && role !== null) {
          Object.keys(role as Record<string, unknown>).forEach((key) => {
            const value = (role as Record<string, unknown>)[key];
            if (
              value === true ||
              value === 'true' ||
              (Array.isArray(value) && value.length > 0)
            ) {
              roleNames.push(key);
            }
          });
        }
      });
    } else if (typeof roles === 'object' && roles !== null) {
      Object.keys(roles as Record<string, unknown>).forEach((key) => {
        const value = (roles as Record<string, unknown>)[key];
        if (
          value === true ||
          value === 'true' ||
          (Array.isArray(value) && value.length > 0)
        ) {
          roleNames.push(key);
        }
      });
    }
    return roleNames;
  }

  private loadWizardData(): void {
    this.loading = true;
    this.error = null;

    const dataSource = this.componentDataSourceService.getDataSource(
      'profile_user_ep_wizard'
    );
    if (!dataSource) {
      this.loading = false;
      this.error = 'Datakilden er ikke tilgængelig.';
      return;
    }

    const params = {
      queryParams: this.userUuid ? { userUuid: this.userUuid } : {},
    };

    this.subscriptions.add(
      dataSource
        .execute(params)
        .pipe(
          finalize(() => {
            this.loading = false;
            this.cdr.markForCheck();
          })
        )
        .subscribe({
          next: (data: ProfileEpWizardDataDTO) => {
            this.wizardData = data;
            if (data.user_uuid) {
              this.userUuid = data.user_uuid;
            }
            this.datesForm.patchValue({
              requirement_started_at:
                data.default_dates?.requirement_started_at ?? '2023-01-01',
              maintenance_started_at:
                data.default_dates?.maintenance_started_at ?? '2023-01-01',
            });
            this.buildPathRows(data.paths ?? [], data.assignments ?? []);
            this.cdr.markForCheck();
          },
          error: () => {
            this.error = 'Kunne ikke hente uddannelsesveje.';
            this.cdr.markForCheck();
          },
        })
    );
  }

  private buildPathRows(
    paths: ProfileEpWizardPathDTO[],
    assignments: ProfileEpWizardAssignmentDTO[]
  ): void {
    const assignmentByPathId = new Map<number, ProfileEpWizardAssignmentDTO>();
    assignments.forEach((a) => assignmentByPathId.set(a.path_id, a));

    this.pathRows = paths.map((path) => {
      const existing = assignmentByPathId.get(path.path_id);
      const versions = path.versions ?? [];
      const defaultVersion =
        existing?.version_id ??
        (versions.length > 0 ? versions[0].version_id : null);
      return {
        path,
        selected: !!existing,
        version_id: defaultVersion,
        isExisting: !!existing,
      };
    });
  }
}
